import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
} from 'react'
import { useLlmStore, llmOpenAI } from '@/stores/llm'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
// ScrollArea removed — plain overflow div provides reliable auto-scroll
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  Send,
  Bot,
  User,
  Trash2,
  Settings,
  RefreshCw,
  Wrench,
  Square,
  Paperclip,
  BookOpen,
  ListTodo,
  Brain,
  History,
  Plus,
  CheckCircle2,
  Circle,
  Clock,
  XCircle,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { cn, getEnv } from '@/lib/utils'
import { gatewayFetch } from '@/lib/gateway-auth'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'

const MarkdownInPreContext = createContext(false)

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  metadata?: {
    model?: string
    tokens?: number
    duration?: number
    operations?: number
    sessionId?: string
  }
}

interface TodoItem {
  id: string
  content: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
}

interface SessionInfo {
  id: string
  title: string
  model: string
  mode: string
  updatedAt: string
  messageCount: number
}

type ChatMode = 'model' | 'apply'
type AgentMode = 'agent' | 'plan'

const CHAT_HISTORY_STORAGE_KEY = 'omtae.chat.history.v1'
const CHAT_MODE_STORAGE_KEY = 'omtae.chat.mode.v1'
const CHAT_WORKSPACE_STORAGE_KEY = 'omtae.chat.workspace.v1'
const AGENT_MODE_STORAGE_KEY = 'omtae.chat.agentmode.v1'

const readStoredMessages = (): Message[] => {
  if (typeof window === 'undefined') return []
  const raw = window.localStorage.getItem(CHAT_HISTORY_STORAGE_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as Array<Omit<Message, 'timestamp'> & { timestamp: string }>
    return parsed.map((message) => ({ ...message, timestamp: new Date(message.timestamp) }))
  } catch { return [] }
}

const persistMessages = (messages: Message[]) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(CHAT_HISTORY_STORAGE_KEY, JSON.stringify(
    messages.map((message) => ({ ...message, timestamp: message.timestamp.toISOString() }))
  ))
}

const readStoredMode = (): ChatMode => {
  if (typeof window === 'undefined') return 'model'
  const value = window.localStorage.getItem(CHAT_MODE_STORAGE_KEY)
  return value === 'apply' ? 'apply' : 'model'
}

const readStoredAgentMode = (): AgentMode => {
  if (typeof window === 'undefined') return 'agent'
  const value = window.localStorage.getItem(AGENT_MODE_STORAGE_KEY)
  return value === 'plan' ? 'plan' : 'agent'
}

const readStoredWorkspace = () => {
  if (typeof window === 'undefined') return getEnv('VITE_CODING_WORKSPACE') || '/home/jay'
  return window.localStorage.getItem(CHAT_WORKSPACE_STORAGE_KEY) || getEnv('VITE_CODING_WORKSPACE') || '/home/jay'
}

const getGatewayBaseUrl = () => {
  const configured = getEnv('VITE_GATEWAY_URL')
  if (configured) return configured.replace(/\/$/, '')
  if (typeof window === 'undefined') return 'http://localhost:8080'
  const { protocol, hostname, port } = window.location
  if (port === '3002' || port === '3001' || port === '5173') return `${protocol}//${hostname}:8080`
  return `${protocol}//${window.location.host}`
}

function extractChatCompletionAssistantText(data: unknown): string {
  if (data === null || typeof data !== 'object') return 'Invalid response from the language model.'
  const o = data as Record<string, unknown>
  const choices = o.choices
  if (!Array.isArray(choices) || choices.length === 0) return 'The model returned no choices.'
  const first = choices[0]
  if (!first || typeof first !== 'object') return 'Malformed choice entry.'
  const message = (first as Record<string, unknown>).message
  if (!message || typeof message !== 'object') return 'Missing message object.'
  const content = (message as Record<string, unknown>).content
  if (typeof content === 'string') return content
  return content === null || content === undefined ? 'Empty message.' : String(content)
}

const CHARS_PER_TOKEN_EST = 3.5

function estimateTokenCount(text: string): number {
  return Math.ceil(text.length / CHARS_PER_TOKEN_EST) + 4
}

function isModel404(errorText: string): boolean {
  return /\b404\b/.test(errorText) && /does not exist|not found/i.test(errorText)
}

function isContextLengthError(errorText: string): boolean {
  return /context length|max.*token|too many tokens|context is full|context window|exceeds model context/i.test(errorText)
}

function formatCodingAgentError(raw: string): string {
  const t = raw.trim()
  if (isContextLengthError(t)) {
    return 'The request hit the model context limit. Start a new chat (New), shorten messages, or raise CODE_LLM_CONTEXT_TOKENS / vLLM --max-model-len if the model supports it. The header shows the gateway context budget when Apply mode is active.'
  }
  if (/LLM unreachable|Context is full/i.test(t)) {
    return t
  }
  return t
}

const TodoStatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'completed': return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
    case 'in_progress': return <Clock className="h-3.5 w-3.5 text-yellow-500" />
    case 'cancelled': return <XCircle className="h-3.5 w-3.5 text-red-400" />
    default: return <Circle className="h-3.5 w-3.5 text-muted-foreground" />
  }
}

export default function ChatPage() {
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<Message[]>(() => readStoredMessages())
  const [isTyping, setIsTyping] = useState(false)
  const [temperature, setTemperature] = useState(0.2)
  const [maxTokens, setMaxTokens] = useState(4096)
  const [showSettings, setShowSettings] = useState(false)
  const [chatMode, setChatMode] = useState<ChatMode>(() => readStoredMode())
  const [agentMode, setAgentMode] = useState<AgentMode>(() => readStoredAgentMode())
  const [codingWorkspace, setCodingWorkspace] = useState(() => readStoredWorkspace())
  const [uploadHint, setUploadHint] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | undefined>()
  const [todos, setTodos] = useState<TodoItem[]>([])
  const [sessions, setSessions] = useState<SessionInfo[]>([])
  const [showSessions, setShowSessions] = useState(false)
  const [showTodos, setShowTodos] = useState(false)
  const [codingAgentConfig, setCodingAgentConfig] = useState<{
    contextLimitTokens: number
    mcpToolCount?: number
    mcpEnabled?: boolean
    runCommandEnabled?: boolean
  } | null>(null)

  const { models, currentModel, fetchModels, setCurrentModel, isLoading, error } = useLlmStore()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const requestAbortRef = useRef<AbortController | null>(null)

  useEffect(() => { fetchModels() }, [fetchModels])

  useEffect(() => {
    let cancelled = false
    const loadCodingConfig = async () => {
      try {
        const res = await gatewayFetch(`${getGatewayBaseUrl()}/coding/config`)
        if (!res.ok || cancelled) return
        const data = (await res.json()) as {
          contextLimitTokens?: number
          mcpToolCount?: number
          mcpEnabled?: boolean
          runCommandEnabled?: boolean
        }
        if (cancelled || typeof data.contextLimitTokens !== 'number') return
        setCodingAgentConfig({
          contextLimitTokens: data.contextLimitTokens,
          mcpToolCount: data.mcpToolCount,
          mcpEnabled: data.mcpEnabled,
          runCommandEnabled: data.runCommandEnabled,
        })
      } catch {
        if (!cancelled) setCodingAgentConfig(null)
      }
    }
    void loadCodingConfig()
    const id = window.setInterval(loadCodingConfig, 120_000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [])
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, isTyping])
  useEffect(() => { persistMessages(messages) }, [messages])
  useEffect(() => { if (typeof window !== 'undefined') window.localStorage.setItem(CHAT_MODE_STORAGE_KEY, chatMode) }, [chatMode])
  useEffect(() => { if (typeof window !== 'undefined') window.localStorage.setItem(AGENT_MODE_STORAGE_KEY, agentMode) }, [agentMode])
  useEffect(() => { if (typeof window !== 'undefined') window.localStorage.setItem(CHAT_WORKSPACE_STORAGE_KEY, codingWorkspace) }, [codingWorkspace])

  const fetchSessions = useCallback(async () => {
    try {
      const res = await gatewayFetch(`${getGatewayBaseUrl()}/coding/sessions`)
      if (res.ok) {
        const data = await res.json()
        setSessions(data.sessions || [])
      }
    } catch { /* ignore */ }
  }, [])

  const resumeSession = useCallback(async (sid: string) => {
    try {
      const res = await gatewayFetch(`${getGatewayBaseUrl()}/coding/sessions/${sid}`)
      if (res.ok) {
        const data = await res.json()
        setSessionId(sid)
        const restored: Message[] = (data.messages || [])
          .filter((m: { role: string }) => m.role !== 'system')
          .map((m: { role: string; content?: string; timestamp?: string }, i: number) => ({
            id: `restored_${i}`,
            role: m.role as 'user' | 'assistant',
            content: m.content || '',
            timestamp: new Date(m.timestamp || Date.now()),
          }))
        setMessages(restored)
        if (data.metadata?.todos) setTodos(data.metadata.todos)
        if (data.metadata?.mode) setAgentMode(data.metadata.mode)
        setShowSessions(false)
      }
    } catch { /* ignore */ }
  }, [])

  const handleSend = async () => {
    if (!input.trim() || isTyping) return
    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setIsTyping(true)
    const controller = new AbortController()
    requestAbortRef.current = controller

    const sendModelChat = async (modelName: string, retry: boolean): Promise<void> => {
      const startTime = Date.now()

      if (chatMode === 'apply') {
        const conversationTokens = messages.reduce((n, m) => n + estimateTokenCount(m.content), 0) + estimateTokenCount(input)
        const safeMaxTokens = Math.min(maxTokens, Math.max(256, 4096 - conversationTokens))

        const response = await gatewayFetch(`${getGatewayBaseUrl()}/coding/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            prompt: input,
            model: modelName,
            temperature,
            maxTokens: safeMaxTokens,
            mode: agentMode,
            sessionId,
            requireWrite: agentMode === 'agent' && /\b(apply|edit|fix|update|change|refactor|implement|create|add|remove|rename|patch)\b/i.test(input),
            workspacePath: codingWorkspace || undefined,
            messages: messages.filter((m) => m.role !== 'system').map((m) => ({ role: m.role, content: m.content })),
          }),
        })
        if (!response.ok) {
          const errText = await response.text()
          if (!retry && isModel404(errText)) {
            await fetchModels()
            const { models: refreshed, currentModel: fallback } = useLlmStore.getState()
            if (refreshed.length > 0 && fallback !== modelName) {
              return sendModelChat(fallback, true)
            }
          }
          throw new Error(`Apply mode failed (${response.status}): ${formatCodingAgentError(errText)}`)
        }
        const data = await response.json() as {
          reply?: string
          sessionId?: string
          todos?: TodoItem[]
          model?: string
          operations?: Array<{ action: string; path?: string; summary?: string }>
          contextLimitTokens?: number
          mcpToolCount?: number
        }
        const duration = Date.now() - startTime

        if (typeof data.contextLimitTokens === 'number') {
          setCodingAgentConfig((prev) =>
            prev
              ? { ...prev, contextLimitTokens: data.contextLimitTokens! }
              : {
                  contextLimitTokens: data.contextLimitTokens!,
                  mcpToolCount: data.mcpToolCount,
                }
          )
        }

        if (data.sessionId) setSessionId(data.sessionId)
        if (data.todos) setTodos(data.todos)

        const replyBody = typeof data.reply === 'string' ? data.reply : 'No response.'
        const replyDisplay = isContextLengthError(replyBody) ? formatCodingAgentError(replyBody) : replyBody

        const opSummary = Array.isArray(data.operations) && data.operations.length > 0
          ? `\n\n**Operations:**\n${data.operations.map((op: { action: string; path?: string; summary?: string }) => `- \`${op.action}\`${op.path ? ` \`${op.path}\`` : ''}${op.summary ? ` — ${op.summary}` : ''}`).join('\n')}`
          : ''

        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `${replyDisplay}${opSummary}`,
          timestamp: new Date(),
          metadata: {
            model: data.model || modelName,
            duration,
            operations: Array.isArray(data.operations) ? data.operations.length : 0,
            sessionId: data.sessionId,
          },
        }
        setMessages((prev) => [...prev, assistantMessage])
      } else {
        const apiMessages = messages
          .filter((m) => m.role !== 'system')
          .map((m) => ({ role: m.role, content: m.content }))
          .concat([{ role: 'user', content: input }])

        const conversationTokens = apiMessages.reduce((n, m) => n + estimateTokenCount(m.content), 0)
        const safeMaxTokens = Math.min(maxTokens, Math.max(256, 4096 - conversationTokens))

        try {
          const response = await llmOpenAI.chatCompletions(apiMessages, {
            model: modelName, temperature, max_tokens: safeMaxTokens, signal: controller.signal,
          })
          const duration = Date.now() - startTime
          const completion = response as { model?: string; usage?: { total_tokens?: number } }
          const assistantMessage: Message = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: extractChatCompletionAssistantText(response),
            timestamp: new Date(),
            metadata: { model: completion.model, tokens: completion.usage?.total_tokens, duration },
          }
          setMessages((prev) => [...prev, assistantMessage])
        } catch (chatErr) {
          const errMsg = (chatErr as Error).message || ''
          if (!retry && isModel404(errMsg)) {
            await fetchModels()
            const { models: refreshed, currentModel: fallback } = useLlmStore.getState()
            if (refreshed.length > 0 && fallback !== modelName) {
              return sendModelChat(fallback, true)
            }
          }
          if (isContextLengthError(errMsg)) {
            throw new Error('Context too long — try clearing chat history or reducing message length.')
          }
          throw chatErr
        }
      }
    }

    try {
      await sendModelChat(currentModel, false)
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: 'Generation stopped.', timestamp: new Date(), metadata: { model: 'cancelled' } }])
        return
      }
      const raw = (err as Error).message || String(err)
      const msg = chatMode === 'apply' ? formatCodingAgentError(raw) : raw
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: `Error: ${msg}`, timestamp: new Date(), metadata: { model: 'error' } }])
    } finally {
      requestAbortRef.current = null
      setIsTyping(false)
    }
  }

  const handleStop = () => { requestAbortRef.current?.abort() }

  const handleWorkspaceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploadHint('Uploading…')
    try {
      const form = new FormData()
      form.append('workspacePath', codingWorkspace.trim())
      form.append('relativePath', 'uploads')
      form.append('file', file)
      const res = await gatewayFetch(`${getGatewayBaseUrl()}/coding/upload`, { method: 'POST', body: form })
      const text = await res.text()
      if (!res.ok) {
        let msg = text
        try {
          const j = JSON.parse(text) as { message?: string }
          if (j.message) msg = j.message
        } catch (_parseError) {
          // Keep the raw response text when payload is not JSON.
        }
        throw new Error(msg || res.statusText)
      }
      const data = JSON.parse(text) as { path: string; size?: number }
      setUploadHint(`Saved ${data.path} (${data.size ?? file.size} bytes)`)
      setInput((prev) => (prev ? `${prev.trimEnd()}\n\n` : '') + `[Uploaded: ${data.path}] `)
      textareaRef.current?.focus()
    } catch (err) { setUploadHint(`Upload failed: ${(err as Error).message}`) }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    if (textareaRef.current) { textareaRef.current.style.height = 'auto'; textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px' }
  }

  const clearMessages = () => { setMessages([]); setSessionId(undefined); setTodos([]) }
  const newSession = () => { clearMessages(); setSessionId(undefined) }

  const isConnected = !error && models.length > 0

  return (
    <div className="h-[calc(100vh-6rem)] lg:h-[calc(100vh-6rem)] h-[calc(100vh-4rem)] flex flex-col">
      {/* Header — compact on mobile */}
      <div className="flex flex-col gap-2 mb-2 lg:mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-xl lg:text-3xl font-bold shrink-0">Chat</h1>
            <span className={cn('h-2 w-2 rounded-full shrink-0', isConnected ? 'bg-green-500' : 'bg-red-500')} />
            <span className="text-xs lg:text-sm text-muted-foreground truncate">
              {isConnected
                ? chatMode === 'apply'
                  ? `${agentMode === 'plan' ? 'Plan' : 'Agent'} · ${currentModel}`
                  : currentModel
                : error || 'Connecting...'}
            </span>
            {sessionId && <Badge variant="outline" className="text-xs shrink-0 hidden sm:inline-flex">Session</Badge>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0 lg:h-auto lg:w-auto lg:px-3" onClick={newSession} title="New chat">
              <Plus className="h-4 w-4" /><span className="hidden lg:inline ml-1">New</span>
            </Button>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setShowSettings(!showSettings)} title="Settings">
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={clearMessages} disabled={messages.length === 0} title="Clear">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {chatMode === 'apply' && codingAgentConfig && (
          <p className="text-xs text-muted-foreground leading-snug -mt-1">
            Gateway context budget ~{codingAgentConfig.contextLimitTokens.toLocaleString()} tokens
            {codingAgentConfig.mcpEnabled
              ? ` · MCP tools: ${codingAgentConfig.mcpToolCount ?? 0}`
              : ' · MCP off'}
            {codingAgentConfig.runCommandEnabled ? ' · run_command on' : ''}
          </p>
        )}
        {/* Controls row — scrollable on mobile */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          {chatMode === 'apply' && (
            <Button
              variant={agentMode === 'plan' ? 'secondary' : 'outline'}
              size="sm"
              className="shrink-0 h-8"
              onClick={() => setAgentMode(agentMode === 'plan' ? 'agent' : 'plan')}
            >
              <BookOpen className="h-3.5 w-3.5 mr-1" />
              {agentMode === 'plan' ? 'Plan' : 'Agent'}
            </Button>
          )}
          <Select value={chatMode} onValueChange={(v) => setChatMode(v as ChatMode)}>
            <SelectTrigger className="w-[140px] lg:w-[180px] h-8 shrink-0">
              <SelectValue placeholder="Chat mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="model">Model-only</SelectItem>
              <SelectItem value="apply">Agent (gateway)</SelectItem>
            </SelectContent>
          </Select>
          {chatMode === 'apply' && (
            <>
              <Button variant="outline" size="sm" className="shrink-0 h-8 w-8 p-0" onClick={() => { fetchSessions(); setShowSessions(!showSessions) }} title="Sessions">
                <History className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="shrink-0 h-8" onClick={() => setShowTodos(!showTodos)} disabled={todos.length === 0}>
                <ListTodo className="h-3.5 w-3.5" />
                {todos.length > 0 && <span className="ml-1 text-xs">{todos.length}</span>}
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" className="shrink-0 h-8 w-8 p-0" onClick={() => fetchModels()} disabled={isLoading} title="Refresh models">
            <RefreshCw className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Sessions panel */}
      {showSessions && sessions.length > 0 && (
        <Card className="mb-4">
          <CardContent className="p-3">
            <div className="text-sm font-medium mb-1">
              Recent sessions
              <span className="text-muted-foreground font-normal"> ({sessions.length} on disk)</span>
            </div>
            {sessions.length > 10 && (
              <p className="text-xs text-muted-foreground mb-2">
                Showing 10 most recently updated. Each &quot;New&quot; chat or cleared history starts a new saved session.
              </p>
            )}
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {sessions.slice(0, 10).map((s) => (
                <div key={s.id} className="flex items-center justify-between p-2 rounded hover:bg-muted cursor-pointer" onClick={() => resumeSession(s.id)}>
                  <div>
                    <span className="text-sm">{s.title}</span>
                    <span className="text-xs text-muted-foreground ml-2">{s.messageCount} messages</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">{s.mode}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(s.updatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Todos panel */}
      {showTodos && todos.length > 0 && (
        <Card className="mb-4">
          <CardContent className="p-3">
            <div className="text-sm font-medium mb-2">Task List</div>
            <div className="space-y-1">
              {todos.map((t) => (
                <div key={t.id} className="flex items-center space-x-2 text-sm p-1">
                  <TodoStatusIcon status={t.status} />
                  <span className={cn(t.status === 'completed' && 'line-through text-muted-foreground', t.status === 'cancelled' && 'line-through text-muted-foreground')}>
                    {t.content}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings */}
      {showSettings && (
        <Card className="mb-4">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Model</label>
                <Select value={currentModel} onValueChange={setCurrentModel}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {models.map((model) => (
                      <SelectItem key={model.name} value={model.name}>{model.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {chatMode === 'apply' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Workspace Path</label>
                  <Textarea value={codingWorkspace} onChange={(e) => setCodingWorkspace(e.target.value)} placeholder="/workspaces/default" className="min-h-[40px] max-h-[80px]" />
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium">Temperature: {temperature}</label>
                <Slider value={[temperature]} onValueChange={([v]) => setTemperature(v)} min={0} max={2} step={0.1} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Max Tokens: {maxTokens}</label>
                <Slider value={[maxTokens]} onValueChange={([v]) => setMaxTokens(v)} min={256} max={16384} step={256} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chat area */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Start a conversation</p>
                <p className="text-sm mt-1">
                  {chatMode === 'apply'
                    ? `${agentMode === 'plan' ? 'Plan mode (read-only)' : 'Agent mode (full tools)'} — ${currentModel}`
                    : `Chat with ${currentModel} via vLLM`}
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setInput('List the files in this workspace')}>
                    <Brain className="h-3 w-3 mr-1" />Explore workspace
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setInput('Search for TODO comments in the codebase')}>
                    Find TODOs
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setInput('Explain the project structure')}>
                    Explain structure
                  </Button>
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div key={message.id} className={cn('flex gap-2 lg:gap-3', message.role === 'user' ? 'justify-end' : 'justify-start')}>
                {message.role !== 'user' && (
                  <Avatar className="h-7 w-7 lg:h-8 lg:w-8 shrink-0 mt-1">
                    <AvatarFallback className="bg-primary text-primary-foreground"><Bot className="h-3.5 w-3.5 lg:h-4 lg:w-4" /></AvatarFallback>
                  </Avatar>
                )}
                <div className={cn('max-w-[90%] lg:max-w-[80%] rounded-lg p-3 lg:p-4', message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted')}>
                  {message.role === 'assistant' ? (
                    <ReactMarkdown
                      className="prose prose-sm prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground/90 prose-a:text-primary prose-strong:text-foreground"
                      components={{
                        pre({ children }) { return <MarkdownInPreContext.Provider value={true}><pre>{children}</pre></MarkdownInPreContext.Provider> },
                        code({ className, children, ...props }) {
                          const isInline = (props as { inline?: boolean }).inline === true
                          const inPre = useContext(MarkdownInPreContext)
                          const match = /language-(\w+)/.exec(className || '')
                          return !isInline && inPre && match ? (
                            <SyntaxHighlighter style={vscDarkPlus as unknown as { [key: string]: CSSProperties }} language={match[1]} PreTag="div">
                              {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                          ) : (<code className={className} {...props}>{children}</code>)
                        },
                      }}
                    >{message.content}</ReactMarkdown>
                  ) : (<p className="whitespace-pre-wrap">{message.content}</p>)}
                  {message.metadata && (
                    <div className="mt-2 flex items-center space-x-2 text-xs opacity-70 flex-wrap">
                      {message.metadata.model && <Badge variant="secondary" className="text-xs">{message.metadata.model}</Badge>}
                      {message.metadata.tokens && <span>{message.metadata.tokens} tokens</span>}
                      {message.metadata.duration && <span>{message.metadata.duration}ms</span>}
                      {typeof message.metadata.operations === 'number' && message.metadata.operations > 0 && <span>{message.metadata.operations} ops</span>}
                    </div>
                  )}
                </div>
                {message.role === 'user' && (
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-secondary"><User className="h-4 w-4" /></AvatarFallback></Avatar>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="flex space-x-3 justify-start">
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary text-primary-foreground"><Bot className="h-4 w-4" /></AvatarFallback></Avatar>
                <div className="bg-muted rounded-lg p-4">
                  <div className="flex space-x-1">
                    <div className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce" />
                    <div className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce delay-100" />
                    <div className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce delay-200" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <CardContent className="border-t p-3 lg:p-4 space-y-2">
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleWorkspaceUpload} />
          {uploadHint && <p className="text-xs text-muted-foreground">{uploadHint}</p>}
          <div className="flex gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={`Message ${currentModel}...`}
              className="min-h-[48px] lg:min-h-[60px] max-h-[150px] lg:max-h-[200px] resize-none text-base"
              disabled={!isConnected}
            />
            <div className="flex flex-col gap-1.5">
              <Button type="button" variant="outline" className="h-10 w-10 p-0 shrink-0" title="Upload file" onClick={() => fileInputRef.current?.click()}>
                <Paperclip className="h-4 w-4" />
              </Button>
              <Button
                onClick={isTyping ? handleStop : handleSend}
                disabled={isTyping ? false : (!input.trim() || !isConnected)}
                className="h-10 w-10 p-0 shrink-0 flex-1"
                variant={isTyping ? 'destructive' : 'default'}
              >
                {isTyping ? <Square className="h-5 w-5" /> : chatMode === 'apply' ? <Wrench className="h-5 w-5" /> : <Send className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
